// Overview.jsx
import { useState } from 'react';
import s from '../shared.module.css';
import o from './overview.module.css';
import PatientHeader from '../PatientHeader';
import HealthTimeline from '../HealthTimeline';
import CarePlanSection from '../CarePlanSection';

const TABS = ['Overview', 'Menstrual Health', 'Reproductive Health', 'Assessments', 'Labs & Reports', 'Medications', 'Lifestyle', 'Notes'];

const riskItems = [
  { icon: '🔵', name: 'PCOS Risk', badge: 'Moderate Risk', badgeClass: 'badgeOrange', score: 58, bar: 58, barClass: 'riskBarOrange' },
  { icon: '🤰', name: 'Pregnancy Risk', badge: 'Low Risk', badgeClass: 'badgeGreen', score: 22, bar: 22, barClass: 'riskBarGreen' },
  { icon: '🦋', name: 'Thyroid Risk', badge: 'Low Risk', badgeClass: 'badgeGreen', score: 18, bar: 18, barClass: 'riskBarGreen' },
  { icon: '🩸', name: 'Anemia Risk', badge: 'Low Risk', badgeClass: 'badgeGreen', score: 24, bar: 24, barClass: 'riskBarGreen' },
  { icon: '🧠', name: 'Mental Health Risk', badge: 'Low Risk', badgeClass: 'badgeGreen', score: 20, bar: 20, barClass: 'riskBarGreen' },
  { icon: '❤️', name: 'Metabolic Risk', badge: 'Moderate Risk', badgeClass: 'badgeOrange', score: 52, bar: 52, barClass: 'riskBarOrange' },
];

const aiActions = [
  { icon: '🧪', title: 'Complete TSH Test', desc: 'Thyroid screening is recommended before conception.', btn: 'Order Test' },
  { icon: '🔵', title: 'PCOS Management', desc: 'Your symptoms indicate moderate PCOS risk. Early management helps improve fertility.', btn: 'Start Program' },
  { icon: '📅', title: 'Book Preconception Consultation', desc: 'Discuss fertility, nutrition and lifestyle readiness.', btn: 'Book Appointment' },
  { icon: '🥗', title: 'Lifestyle Optimization', desc: 'Personalized plan for nutrition, exercise, sleep and stress management.', btn: 'View Plan' },
  { icon: '💊', title: 'Folic Acid Supplementation', desc: 'Continue folic acid for at least 3 months before conception.', btn: 'Learn More' },
];

export default function Overview() {
  const [activeTab, setActiveTab] = useState('Overview');

  return (
    <div className={s.pageWrap}>
      {/* Page header */}
      <div className={s.pageHead}>
        <div className={s.pageHeadLeft}>
          <h1 className={s.pageTitle}>Unified Women's Health Record</h1>
          <p className={s.pageSubtitle}>Comprehensive • Lifelong • Personalized</p>
        </div>
        <div className={s.pageHeadActions}>
          <button className={s.btnOutline}>🔗 Share Record</button>
          <button className={s.btnPrimary}>+ Add Note</button>
        </div>
      </div>

      {/* Patient card */}
      <PatientHeader
        summaryFields={[
          { groupLabel: 'Record Summary', label: 'Blood Group', value: 'O+' },
          { label: 'Height / Weight', value: '162 cm / 68 kg' },
          { label: 'BMI', value: '25.9 (Overweight)' },
          { label: 'Last Visit', value: '12 May 2026' },
        ]}
      />

      {/* Life Stage Snapshot */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Life Stage Snapshot <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ava-color-caption)' }}>ⓘ</span></h2>
        </div>
        <div className={o.overviewGrid}>
          {/* Current Life Stage */}
          <div className={o.snapshotCard}>
            <p className={o.snapshotLabel} style={{ color: 'var(--ava-color-primary-700)' }}>Current Life Stage</p>
            <p className={o.snapshotValue}>Reproductive Age</p>
            <div style={{ margin: '8px 0', fontSize: 32 }}>🌸</div>
            <p className={o.snapshotSub}>Stage since Jan 2023</p>
          </div>
          {/* Active Journey */}
          <div className={o.snapshotCard}>
            <p className={o.snapshotLabel} style={{ color: 'var(--ava-color-success-500)' }}>Active Journey</p>
            <p style={{ fontSize: 16, fontWeight: 700, color: 'var(--ava-color-success-500)', margin: '4px 0 8px' }}>Preconception</p>
            <div style={{ fontSize: 28, margin: '4px 0' }}>💜</div>
            <p className={o.snapshotSub}>Goal: Plan pregnancy in next 6 months</p>
          </div>
          {/* Key Risks */}
          <div className={o.snapshotCard}>
            <p className={o.snapshotLabel} style={{ color: 'var(--ava-color-error-500)' }}>Key Risks</p>
            {[{ name: 'PCOS Risk', level: 'Moderate', cls: 'badgeOrange' }, { name: 'Thyroid Risk', level: 'Low', cls: 'badgeGreen' }, { name: 'Anemia Risk', level: 'Low', cls: 'badgeGreen' }].map((r, i) => (
              <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', borderBottom: i < 2 ? '1px solid var(--ava-color-gray-100)' : 'none', fontSize: 11 }}>
                <span style={{ color: 'var(--ava-color-body)' }}>{r.name}</span>
                <span className={s[r.cls]}>{r.level}</span>
              </div>
            ))}
            <button className={s.sectionLink} style={{ marginTop: 6, fontSize: 10 }}>View all (5)</button>
          </div>
          {/* Due Actions */}
          <div className={o.snapshotCard}>
            <p className={o.snapshotLabel} style={{ color: 'var(--ava-color-warning-500)' }}>Due Actions</p>
            {[{ name: 'Pap Smear', s: 'badgeRed' }, { name: 'TSH Test', s: 'badgeRed' }, { name: 'Vitamin D Test', s: 'badgeRed' }].map((a, i) => (
              <div key={i} className={o.dueRow}>
                <span className={o.dueName}><span>☐</span>{a.name}</span>
                <span className={s[a.s]}>Due</span>
              </div>
            ))}
          </div>
          {/* Next Appointment */}
          <div className={o.apptCard}>
            <p className={o.snapshotLabel} style={{ color: 'var(--ava-color-primary-700)' }}>Next Appointment</p>
            <div className={o.apptDate}>
              <span className={o.apptDay}>20</span>
              <span className={o.apptMonth}>May</span>
            </div>
            <p className={o.apptDoctor}>Dr. Ananya Sharma</p>
            <p className={o.apptSpec}>Gynecologist</p>
            <p className={o.apptTime}>10:30 AM</p>
            <button className={o.apptReschedule}>Reschedule</button>
          </div>
        </div>
      </div>

      {/* Risk Summary */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Risk Summary <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ava-color-caption)' }}>ⓘ</span></h2>
          <span style={{ fontSize: 10, color: 'var(--ava-color-caption)', display: 'flex', alignItems: 'center', gap: 4 }}>
            Risk Model: Avexa Women's Health AI ✨
          </span>
        </div>
        <div className={o.riskGrid}>
          {riskItems.map((r, i) => (
            <div key={i} className={o.riskItem}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
                <span style={{ fontSize: 18 }}>{r.icon}</span>
                <span className={s[r.badgeClass]}>{r.badge}</span>
              </div>
              <p className={o.riskName}>{r.name}</p>
              <p className={o.riskScore}>Score {r.score} /100</p>
              <div className={s.riskBar}>
                <div className={`${s.riskBarFill} ${s[r.barClass]}`} style={{ width: `${r.bar}%` }} />
              </div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: 'right', marginTop: 10 }}>
          <button className={s.sectionLink}>View full risk report →</button>
        </div>
      </div>

      {/* Women's Health Timeline */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Women's Health Timeline <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ava-color-caption)' }}>ⓘ</span></h2>
          <button className={s.btnGhost}>View Full Timeline</button>
        </div>
        <HealthTimeline />
      </div>

      {/* Tabs */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.tabBar}>
          {TABS.map(t => (
            <button key={t} className={`${s.tab} ${activeTab === t ? s.tabActive : ''}`} onClick={() => setActiveTab(t)}>{t}</button>
          ))}
        </div>

        {activeTab === 'Overview' && (
          <div className={o.overviewTabContent}>
            {/* Active conditions */}
            <div className={o.overviewTabCol}>
              <p className={o.overviewColTitle}>Active Conditions</p>
              {['Irregular Periods', 'PCOS (Polycystic Ovary Syndrome)', 'Vitamin D Deficiency'].map((c, i) => (
                <div key={i} className={o.conditionItem}>
                  <span style={{ color: 'var(--ava-color-error-500)', fontSize: 12 }}>●</span> {c}
                </div>
              ))}
              <button className={s.sectionLink} style={{ marginTop: 8, fontSize: 11 }}>View all conditions →</button>

              <p className={o.overviewColTitle} style={{ marginTop: 14 }}>Current Medications</p>
              {[
                { name: 'Metformin 500mg', dose: '1-0-1' },
                { name: 'Vitamin D3 60,000 IU', dose: 'Weekly' },
                { name: 'Folic Acid 5mg', dose: '1-0-0' },
              ].map((m, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, padding: '4px 0', borderBottom: '1px solid var(--ava-color-gray-100)' }}>
                  <span style={{ color: 'var(--ava-color-body)' }}>{m.name}</span>
                  <span style={{ color: 'var(--ava-color-heading)', fontWeight: 600 }}>{m.dose}</span>
                </div>
              ))}
              <button className={s.sectionLink} style={{ marginTop: 8, fontSize: 11 }}>View all medications →</button>
            </div>

            {/* Recent Labs */}
            <div className={o.overviewTabCol}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <p className={o.overviewColTitle}>Recent Labs</p>
                <button className={s.sectionLink} style={{ fontSize: 10 }}>View all</button>
              </div>
              {[
                { name: 'TSH', val: '2.45 mIU/L', date: '05 May 2026', status: 'Normal', cls: 'badgeGreen' },
                { name: 'HbA1c', val: '5.6 %', date: '05 May 2026', status: 'Normal', cls: 'badgeGreen' },
                { name: 'Vitamin D', val: '18 ng/mL', date: '05 May 2026', status: 'Low', cls: 'badgeRed' },
                { name: 'Hemoglobin', val: '11.2 g/dL', date: '05 May 2026', status: 'Low', cls: 'badgeRed' },
              ].map((l, i) => (
                <div key={i} className={o.labRow}>
                  <span className={o.labName}>{l.name}</span>
                  <span className={o.labValue}>{l.val}</span>
                  <span className={o.labDate}>{l.date}</span>
                  <span className={s[l.cls]}>{l.status}</span>
                </div>
              ))}

              <p className={o.overviewColTitle} style={{ marginTop: 14 }}>Recent Assessments</p>
              {[
                { name: 'PHQ-9 Score', score: 6, lvl: 'Mild', date: '10 May 2026', cls: 'badgeOrange' },
                { name: 'GAD-7 Score', score: 5, lvl: 'Mild', date: '10 May 2026', cls: 'badgeOrange' },
                { name: 'PCOS Assessment', score: null, lvl: 'Moderate Risk', date: '10 May 2026', cls: 'badgeOrange' },
              ].map((a, i) => (
                <div key={i} className={o.assessRow}>
                  <span className={o.assessName}>{a.name}</span>
                  {a.score !== null && <span className={o.assessScore}>{a.score}</span>}
                  <span className={s[a.cls]}>{a.lvl}</span>
                  <span className={o.labDate}>{a.date}</span>
                </div>
              ))}
              <button className={s.sectionLink} style={{ marginTop: 8, fontSize: 11 }}>View all assessments →</button>
            </div>

            {/* Health Highlights + Preventive */}
            <div className={o.overviewTabCol}>
              <p className={o.overviewColTitle}>Health Highlights</p>
              {[
                'Cycles are irregular (35–45 days)',
                'Experiencing acne and hair fall',
                'Weight gain of 6 kg in last 1 year',
                'No known drug allergies',
                'Sleep quality: Average (6 hrs/night)',
              ].map((h, i) => (
                <div key={i} className={o.highlightItem}>
                  <span className={o.highlightCheck}>✓</span> {h}
                </div>
              ))}
              <button className={s.sectionLink} style={{ marginTop: 8, fontSize: 11 }}>View all highlights →</button>

              <p className={o.overviewColTitle} style={{ marginTop: 14 }}>Preventive Care</p>
              {[
                { name: 'Pap Smear', status: 'Due', cls: 'badgeRed' },
                { name: 'HPV Vaccination', status: 'Completed', cls: 'badgeGreen' },
                { name: 'Dental Checkup', status: 'Due', cls: 'badgeRed' },
                { name: 'Eye Checkup', status: 'Due', cls: 'badgeRed' },
              ].map((p, i) => (
                <div key={i} className={o.preventiveItem}>
                  <span style={{ color: 'var(--ava-color-body)' }}>{p.name}</span>
                  <span className={s[p.cls]}>{p.status}</span>
                </div>
              ))}
              <button className={s.sectionLink} style={{ marginTop: 8, fontSize: 11 }}>View preventive care →</button>
            </div>
          </div>
        )}

        {activeTab !== 'Overview' && (
          <div style={{ padding: '32px', textAlign: 'center', color: 'var(--ava-color-caption)', fontSize: 13 }}>
            <div style={{ fontSize: 32, marginBottom: 8 }}>📋</div>
            <p>{activeTab} content — select this tab to view detailed data.</p>
          </div>
        )}
      </div>

      {/* AI Recommended Actions */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>AI Recommended Actions <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ava-color-caption)' }}>ⓘ</span></h2>
          <span style={{ fontSize: 10, color: 'var(--ava-color-caption)', display: 'flex', alignItems: 'center', gap: 4 }}>
            Powered by Avexa Women's Health AI ✨
          </span>
        </div>
        <div className={o.aiActionsRow}>
          {aiActions.map((a, i) => (
            <div key={i} className={o.aiActionCard}>
              <div className={o.aiActionIcon}>{a.icon}</div>
              <p className={o.aiActionTitle}>{a.title}</p>
              <p className={o.aiActionDesc}>{a.desc}</p>
              <button className={o.aiActionBtn}>{a.btn}</button>
            </div>
          ))}
        </div>
      </div>

      {/* Care Plan */}
      <div className={s.card} style={{ marginBottom: 16 }}>
        <div className={s.sectionHeader}>
          <h2 className={s.sectionTitle}>Care Plan <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--ava-color-caption)' }}>ⓘ</span></h2>
        </div>
        <CarePlanSection />
      </div>
    </div>
  );
}